import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './RollbackConfirmDialog.css';

const RollbackConfirmDialog = ({ isOpen, targetVersion, versionsToDelete, onConfirm, onCancel, onBackupDownload }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [backupDone, setBackupDone] = useState(false);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setConfirmed(false);
      setBackupDone(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackup = async () => {
    if (onBackupDownload) {
      await onBackupDownload();
      setBackupDone(true);
    }
  };

  return (
    <div className="rollback-dialog-overlay">
      <button type="button" className="rollback-dialog-overlay-btn" onClick={onCancel} aria-label="Dialog schließen" />
      <dialog className="rollback-dialog" open aria-label="Version zurückrollen">
        <div className="rollback-dialog-header">
          <span className="rollback-icon">
            <i className="bi bi-exclamation-octagon-fill"></i>
          </span>
          <h5>Auf Version {targetVersion} zurückrollen?</h5>
          <button className="rollback-dialog-close" onClick={onCancel}>&times;</button>
        </div>

        <div className="rollback-dialog-body">
          {/* Warning box */}
          <div className="rollback-warning">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <div>
              <strong>Achtung — diese Aktion ist unwiderruflich!</strong>
              <ul>
                <li>Der aktuelle Arbeitsstand geht verloren</li>
                <li>
                  {versionsToDelete.length === 1
                    ? `Version v${versionsToDelete[0]} wird unwiderruflich gelöscht`
                    : `${versionsToDelete.length} neuere Versionen werden unwiderruflich gelöscht`}
                </li>
              </ul>
              {versionsToDelete.length > 0 && (
                <div className="rollback-versions-to-delete">
                  <span>Betroffene Versionen: </span>
                  {versionsToDelete.map((v) => (
                    <span key={v} className="rollback-version-badge">v{v}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Info tip */}
          <div className="rollback-info">
            <i className="bi bi-info-circle-fill"></i>
            <span>
              <strong>Tipp:</strong> Ältere Versionen können jederzeit über das{' '}
              <i className="bi bi-eye"></i>-Symbol im View-Modus angesehen werden,
              ohne den aktuellen Stand zu verändern.
            </span>
          </div>

          {/* Backup section */}
          <div className="rollback-backup-section">
            <span className="rollback-backup-label">Aktuellen Stand sichern:</span>
            <button
              className="btn rollback-btn-backup"
              onClick={handleBackup}
              type="button"
            >
              <i className={`bi ${backupDone ? 'bi-check-lg' : 'bi-download'}`}></i>
              {backupDone ? ' Backup heruntergeladen' : ' Aktuellen Stand als Backup herunterladen'}
            </button>
          </div>

          {/* Confirmation checkbox */}
          <label className="rollback-confirm-label">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <span>Ich habe verstanden, dass alle neueren Versionen gelöscht werden</span>
          </label>
        </div>

        <div className="rollback-dialog-footer">
          <button
            className="btn rollback-btn-execute"
            onClick={onConfirm}
            disabled={!confirmed}
            title={confirmed ? `Auf v${targetVersion} zurückrollen` : 'Bitte zuerst die Bestätigung aktivieren'}
          >
            <i className="bi bi-arrow-counterclockwise"></i> Rollback auf v{targetVersion} ausführen
          </button>
          <button className="btn rollback-btn-cancel" onClick={onCancel}>
            Abbrechen
          </button>
        </div>
      </dialog>
    </div>
  );
};

RollbackConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  targetVersion: PropTypes.string,
  versionsToDelete: PropTypes.arrayOf(PropTypes.string),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onBackupDownload: PropTypes.func,
};

export default RollbackConfirmDialog;
